import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/primitives/Button.jsx';
import Input from '@/components/primitives/Input.jsx';
import AppLogo from '@/components/surfaces/AppLogo.jsx';
import ExecutionStatus from '@/components/surfaces/ExecutionStatus.jsx';
import toastManager from '@/lib/toast/toastManager.js';
import answerConnectionCard from '@/redux/actions/pieveActions/answerConnectionCard.js';
import startConnectionSignIn from '@/redux/actions/pieveActions/startConnectionSignIn.js';
import { isThreadRunning } from '@/redux/selectors/pieveSelectors/threadRunSelectors.js';
import buildConnectionDismissSummary from '../lib/buildConnectionDismissSummary.js';
import buildConnectionReturnPath from '../lib/buildConnectionReturnPath.js';

/**
 * Pieve needs an app connected (or reconnected) to go on. The card names the app and offers one
 * button. An app that signs in on its provider's site sends the browser there; the sign-in comes back
 * to this conversation, and `ConnectionReturnHandler` continues it. An app that takes typed values
 * opens its connection page. When the provider's sign-in address is built from the person's own
 * values (Salesforce's domain, Dynamics 365's tenant), the card asks for them first and the same
 * button saves them and opens the sign-in. Dismiss closes the card and tells Pieve no connection changed.
 * Once connected, the card keeps that on record.
 */
const ConnectionCard = ({ connection, threadId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isRunning = useSelector((state) => isThreadRunning(state, threadId));
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [fields, setFields] = useState(null);
  const [values, setValues] = useState({});
  const { app, setup } = connection.connectionSetup;
  const appName = app.name || 'the app';
  const isOpen = !connection.outcome || connection.outcome === 'failed';
  const isConnected = connection.outcome === 'connected';
  const title = `${connection.isReconnect ? 'Reconnect' : 'Connect'} ${appName}`;
  const openDetail = (connection.isReconnect && setup.errorMessage) || connection.prompt || `Pieve needs your ${appName} account to continue.`;
  const detail = isConnected ? `Pieve can now work in ${appName}.` : connection.outcome === 'failed' ? `The last ${appName} sign-in did not finish. Try again.` : openDetail;

  const missingRequired = Boolean(fields?.some((field) => field.required && String(values[field.key] ?? '').trim() === ''));

  const connect = async (event) => {
    event?.preventDefault();
    setBusy('connect');
    setError('');
    const res = await dispatch(
      startConnectionSignIn({
        connectionSetup: connection.connectionSetup,
        values: fields ? values : null,
        returnPath: buildConnectionReturnPath({ threadId, interactionId: connection.interactionId }),
        returnOrigin: window.location.origin
      })
    );

    if (res?.success === false) {
      setBusy('');
      setError(res.message);
      return;
    }

    if (res.data.kind === 'fields') {
      setBusy('');
      setFields(res.data.fields);
      return;
    }

    if (res.data.kind === 'form') {
      navigate(res.data.path);
      return;
    }

    // The provider's sign-in page; the button stays busy until the browser leaves.
    window.location.assign(res.data.url);
  };

  const dismiss = async () => {
    setBusy('dismiss');
    const patch = { outcome: 'dismissed', respondedAt: new Date().toISOString(), summary: buildConnectionDismissSummary({ appName, isReconnect: connection.isReconnect }) };
    const res = await dispatch(answerConnectionCard({ threadId, interactionId: connection.interactionId, patch }));
    setBusy('');

    if (res?.success === false) {
      toastManager.add({ title: 'The card was not dismissed', description: res.message || res.data?.message || 'Try again.', type: 'error' });
    }
  };

  return (
    <section className="assistant-connection" data-state={isConnected ? 'connected' : isOpen ? 'open' : 'closed'} aria-label={title}>
      <header className="assistant-connection__head">
        <AppLogo app={{ icon: app.icon, name: appName }} />
        <div className="assistant-connection__heading">
          <h3 className="assistant-connection__title">{title}</h3>
          <p className="assistant-connection__detail">{detail}</p>
        </div>
        {isConnected ? <ExecutionStatus state="done" size="sm" label="Connected" /> : isOpen ? <ExecutionStatus state="waiting" size="sm" /> : null}
      </header>
      {error && (
        <p className="assistant-connection__error" role="alert">
          {error}
        </p>
      )}
      {isOpen && fields && (
        <form id={`connection-fields-${connection.interactionId}`} className="assistant-connection__fields" onSubmit={connect} noValidate>
          <p className="assistant-connection__lead">{appName} needs these details before its sign-in page opens.</p>
          {fields.map((field) => (
            <Input key={field.key} label={field.name || field.key} hint={field.description || undefined} placeholder={field.placeholder || undefined} type={field.isSensitive ? 'password' : 'text'} autoComplete="off" required={field.required} value={values[field.key] ?? ''} onChange={(changeEvent) => setValues((current) => ({ ...current, [field.key]: changeEvent.target.value }))} />
          ))}
        </form>
      )}
      {isOpen && (
        <div className="assistant-connection__buttons">
          <Button variant="primary" type={fields ? 'submit' : 'button'} form={fields ? `connection-fields-${connection.interactionId}` : undefined} loading={busy === 'connect'} disabled={Boolean(busy) || missingRequired} onClick={fields ? undefined : connect}>
            {title}
          </Button>
          <Button variant="ghost" loading={busy === 'dismiss'} disabled={Boolean(busy) || isRunning} onClick={dismiss}>
            Dismiss
          </Button>
        </div>
      )}
    </section>
  );
};

export default ConnectionCard;
